/**
 * The first-launch age check's rules (producer 2026-10-14): ask only the
 * birth year, unless that year means the player could still be 17; then
 * ask the month, and the day only if that month is this month. Players
 * must be 18 or over.
 *
 * <p>Dates are the device's local calendar. A birthday today counts: the
 * player turned 18 today. Someone born on 29 February has their birthday
 * on 1 March in other years, the stricter of the two readings.
 */

export const ADULT_AGE = 18;

/** How far back the year picker goes. */
export const OLDEST_AGE = 100;

export interface BirthAnswer {
  year: number;
  /** 1 to 12. */
  month?: number;
  /** 1 to 31. */
  day?: number;
}

/** What the answer so far tells us. */
export type AgeVerdict = 'adult' | 'under' | 'ask-month' | 'ask-day';

/** The local calendar date of `now`. */
export interface CalendarDay {
  year: number;
  /** 1 to 12. */
  month: number;
  day: number;
}

export function calendarDay(now: Date): CalendarDay {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/**
 * Judge the answer so far against `today`. Asks for the month only when
 * the year alone can't tell (the player turns 18 this year), and for the
 * day only when they were born in this month.
 */
export function judgeAge(answer: BirthAnswer, today: CalendarDay, minAge: number = ADULT_AGE): AgeVerdict {
  const turning = today.year - answer.year;
  if (turning > minAge) return 'adult';
  if (turning < minAge) return 'under';
  if (answer.month === undefined) return 'ask-month';
  if (answer.month < today.month) return 'adult';
  if (answer.month > today.month) return 'under';
  if (answer.day === undefined) return 'ask-day';
  // A 29 February birthday falls on 1 March in a year without one.
  if (answer.month === 2 && answer.day === 29 && !isLeapYear(today.year)) return 'under';
  return answer.day <= today.day ? 'adult' : 'under';
}

/** The years the picker offers, oldest first. */
export function pickerYears(today: CalendarDay): number[] {
  const years: number[] = [];
  for (let y = today.year - OLDEST_AGE; y <= today.year; y++) years.push(y);
  return years;
}

/** One entry in the year picker's list. */
export interface YearOption {
  /** What the tile shows: a year ("1985") or a decade ("1980s"). */
  label: string;
  /** The digits tapping it types. */
  digits: string;
  kind: 'year' | 'decade';
}

/** The list never holds more than this, so it fits a fixed-height grid. */
export const YEAR_LIST_SIZE = 12;

/**
 * The picker's list for what has been typed so far, narrowing as the
 * player types: the matching years when they fit the list, otherwise the
 * matching decades. A full year shows its decade, so the player can still
 * change it with one tap.
 */
export function yearOptions(typed: string, today: CalendarDay): YearOption[] {
  const prefix = typed.length >= 4 ? typed.slice(0, 3) : typed;
  const matching = pickerYears(today).filter((y) => String(y).startsWith(prefix));
  if (matching.length <= YEAR_LIST_SIZE) {
    return matching.map((y) => ({ label: String(y), digits: String(y), kind: 'year' }));
  }
  const decades: YearOption[] = [];
  for (const y of matching) {
    const decade = Math.floor(y / 10) * 10;
    const digits = String(decade).slice(0, 3);
    if (decades.at(-1)?.digits !== digits) decades.push({ label: `${decade}s`, digits, kind: 'decade' });
  }
  return decades;
}

/** Whether `digit` can follow what has been typed: only digits that lead
 *  to a year the picker offers. */
export function canType(typed: string, digit: string, today: CalendarDay): boolean {
  if (typed.length >= 4) return false;
  const next = typed + digit;
  return pickerYears(today).some((y) => String(y).startsWith(next));
}

/** The year typed, once it is a whole year the picker offers. */
export function typedYear(typed: string, today: CalendarDay): number | null {
  if (typed.length !== 4) return null;
  const year = Number(typed);
  return pickerYears(today).includes(year) ? year : null;
}

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
